// src/components/ProductCard.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StarRating from "./StarRating";
import FavoriteButton from "./FavoriteButton";
import { useCurrency } from "../context/CurrencyContext";
import { formatPrice } from "../utils/formatPrice";
import { useNotification } from "../context/NotificationContext";
import { isCartItem, toggleCartItem } from "../utils/cart";
import { FiShoppingBag, FiCheck, FiMapPin, FiBox, FiClock } from "react-icons/fi";
import { localizedProductName } from "../utils/i18nContent";
import { formatTimeAgo } from "../utils/dateFormatter";
import { formatStockDisplay } from "../utils/stockFormatter";

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { currency, rates } = useCurrency();
  const { showNotification } = useNotification();
  const [inCart, setInCart] = useState(() => isCartItem(product.id));
  const [cartAnimating, setCartAnimating] = useState(false);
  const productName = localizedProductName(product, i18n.language);

  const imageUrl =
    product.thumbnail_url || "https://via.placeholder.com/600x600.png?text=Chafadi";
  const basePrice = product.price || product.max_price || product.min_price || "0";
  const currentPrice = formatPrice(basePrice, currency, rates);

  const stockInfo = formatStockDisplay(product.quantity_in_stock, i18n.language);
  const timeAgoText = formatTimeAgo(product.created_at, i18n.language);

  useEffect(() => {
    const syncCartState = () => setInCart(isCartItem(product.id));
    window.addEventListener("chafadi-cart-updated", syncCartState);
    return () => window.removeEventListener("chafadi-cart-updated", syncCartState);
  }, [product.id]);

  const handleCardClick = (event) => {
    // If the click is inside a button or link, let that element handle it
    if (event.target.closest("button") || event.target.closest("a")) {
      return;
    }
    navigate(`/products/${product.id}`);
  };

  const handleAddToCart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const added = toggleCartItem(product);
    setInCart(added);
    setCartAnimating(true);
    window.setTimeout(() => setCartAnimating(false), 400);
    showNotification(
      added
        ? t("notifications.productAddedToCart", { name: productName })
        : t("notifications.productRemovedFromCart", { name: productName }),
      added ? "success" : "warning"
    );
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group relative w-full flex flex-col bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-[20px] sm:rounded-[22px] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.02)] hover:shadow-[0_10px_28px_rgba(0,0,0,0.06)] hover:border-[var(--color-accent)]/35 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer ${
        cartAnimating ? "scale-[0.98]" : ""
      }`}
    >
      {/* ── 1. PRODUCT MEDIA FRAME (Flush to Top, Left, and Right edges, Zero Padding) ── */}
      <div className="relative w-full aspect-square overflow-hidden bg-transparent">
        <Link to={`/products/${product.id}`} className="block w-full h-full">
          <img
            src={imageUrl}
            alt={productName}
            className="w-full h-full object-cover transition-transform duration-500 ease-[cubic-bezier(0.28,0.11,0.32,1)] group-hover:scale-105"
            loading="lazy"
          />
        </Link>

        {/* Discount Badge */}
        {product.discount_percentage > 0 && (
          <div className="absolute top-2 left-2 z-10 rounded-full bg-gradient-to-r from-[#ff9f0a] to-[#ff5e00] px-2 py-0.5 text-[9px] font-bold text-white shadow-sm tracking-tight">
            -{product.discount_percentage}%
          </div>
        )}

        {/* Favorite Heart Button */}
        <div className="absolute top-2 right-2 z-20">
          <FavoriteButton
            product={product}
            size="sm"
            className="w-7 h-7 flex items-center justify-center rounded-full bg-white/90 dark:bg-black/70 backdrop-blur-md text-[var(--color-text-primary)] hover:scale-110 active:scale-95 transition-all shadow-sm"
          />
        </div>
      </div>

      {/* ── 2. COMPACT PRODUCT INFO (Pic, Name, Location, Rating, Price, Stock, Date, Add to Cart) ── */}
      <div className="p-3 pt-2.5 flex flex-col gap-1 flex-1 justify-between">
        {/* Location & Rating */}
        <div className="flex items-center justify-between gap-1 text-[11px] text-gray-500 dark:text-zinc-400">
          <span className="flex items-center gap-1 truncate max-w-[60%]">
            <FiMapPin size={10} className="text-[var(--color-accent)] shrink-0" />
            <span className="truncate">{product.location || "Niamey"}</span>
          </span>
          <div className="flex items-center gap-1 shrink-0 origin-right scale-90">
            <StarRating rating={product.average_rating || 5} />
          </div>
        </div>

        {/* Product Name */}
        <Link to={`/products/${product.id}`} className="block no-underline">
          <h3 className="text-[13px] sm:text-[14px] font-semibold text-[var(--color-text-primary)] leading-snug truncate group-hover:text-[var(--color-accent)] transition-colors">
            {productName}
          </h3>
        </Link>

        {/* Price */}
        <div className="flex items-center justify-between">
          <span className="text-[14px] sm:text-[15px] font-bold text-[var(--color-text-primary)] tracking-tight">
            {currentPrice}
          </span>
        </div>

        {/* Stock availability (icon + N in Stock) & Clock + Time Ago */}
        <div className="flex items-center justify-between gap-1 text-[10px] sm:text-[10.5px] pt-0.5">
          <span
            className={`inline-flex items-center gap-1 font-semibold ${
              stockInfo.urgency === "out"
                ? "text-red-500"
                : stockInfo.urgency === "low"
                ? "text-amber-600 dark:text-amber-400"
                : "text-emerald-600 dark:text-emerald-400"
            }`}
          >
            <FiBox size={11} className="shrink-0" />
            <span>{stockInfo.cardText}</span>
          </span>
          {timeAgoText && (
            <span className="flex items-center gap-1 truncate text-gray-400 dark:text-zinc-500 text-[10px]">
              <FiClock size={10} className="shrink-0" />
              <span>{timeAgoText}</span>
            </span>
          )}
        </div>

        {/* Add to Cart Button (Sleek Compact Pill Right Under Them!) */}
        <button
          onClick={handleAddToCart}
          className={`mt-1 w-full h-8 rounded-full text-[11px] sm:text-[12px] font-semibold tracking-tight flex items-center justify-center gap-1.5 transition-all duration-200 shadow-sm ${
            inCart
              ? "bg-[var(--color-success)] text-white"
              : "bg-gradient-to-r from-[#ff9f0a] to-[#ff5e00] text-white hover:opacity-95 hover:shadow-[0_2px_10px_rgba(255,106,0,0.3)] active:scale-[0.98]"
          }`}
        >
          {inCart ? (
            <>
              <FiCheck size={13} /> {t("productCard.added", "Added")}
            </>
          ) : (
            <>
              <FiShoppingBag size={13} /> {t("productCard.addToCart", "Add to Cart")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
